import { ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import type { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBusinessRelationshipWithMostAssetsUnderManagement } from '../../api';
import type { ClientSessionDto, Notification } from '../../api/generated/client';
import { useNotifications } from '../../api/notification-api/queries/use-notifications';
import { DEFAULT_INPUT_DATE_FORMAT } from '../../config/constants';
import { useYwuApp } from '../../contexts/ywu-app-context';
import { combineTwoItemsIntoOne } from '../../utils/utils';
import { wnSendTrace } from '../../utils/wn-event-utils';
import { AgGridTable } from '../atoms';
import { SessionTableNotificationCellRenderer } from './SessionTableNotificationCellRenderer';

// title and filter height/margins (124 px) + screen padding top/bottom (80 px)
const HEADER_TITLE_AND_PADDINGS_HEIGHT = 194;

const emptyNotifications: Notification = { dueEvents: 0, overdueEvents: 0 };

interface IOptionData extends Omit<ClientSessionDto, 'mainDomicile' | 'firstName' | 'lastName' | 'createdAt' | 'brNumbers'> {
    fullName: string;
    brNumbers: string;
    notifications: Notification;
    mainDomicile?: string;
}

interface IProps {
    sessions: ClientSessionDto[];
}

export const SessionTable: FunctionComponent<IProps> = ({ sessions }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { isWealthNavigatorMFE, dispatchNavigateToClientContextEvent } = useYwuApp();

    const [selectedSession, setSelectedSession] = useState<IOptionData>();

    const {
        data: targetBrUuid,
        isSuccess,
        isError,
        error,
    } = useBusinessRelationshipWithMostAssetsUnderManagement(selectedSession?.partnerUuid, selectedSession?.brUuids);
    const { data: notifications } = useNotifications();

    useEffect(() => {
        if (!selectedSession) return;
        if (isWealthNavigatorMFE && selectedSession.brNumbers && selectedSession.partnerUuid) {
            if (isSuccess) dispatchNavigateToClientContextEvent?.(targetBrUuid, selectedSession.partnerUuid);
            if (error?.status === 404) navigate(`../${selectedSession.partnerUuid}`);

            wnSendTrace({
                feature: 'client-session',
                tags: { partnerUuid: selectedSession.partnerUuid, action: 'open-session', source: 'session-table', brUuid: targetBrUuid ?? '' },
            });
        } else {
            navigate(`../${selectedSession.partnerUuid}`);
        }
    }, [selectedSession, targetBrUuid, isWealthNavigatorMFE, dispatchNavigateToClientContextEvent, navigate, isError, isSuccess, error?.status]);

    const headCells: ColDef<IOptionData>[] = useMemo(
        () => [
            {
                field: 'fullName',
                headerName: t('name'),
                minWidth: 220,
            },
            {
                field: 'partnerId',
                headerName: t('sessionPartnerId'),
                width: 150,
            },
            {
                field: 'brNumbers',
                headerName: t('sessionRelatedZrId'),
                sortable: false,
                minWidth: 200,
                flex: 1,
                cellStyle: { display: 'flex', alignItems: 'center' },
                cellRenderer: ({ value }: ICellRendererParams<IOptionData, string>) =>
                    value ? (
                        <ZfStack>
                            {value.split(',').map((v) => (
                                <ZfTypography key={v} variant="body-small">
                                    {v}
                                </ZfTypography>
                            ))}
                        </ZfStack>
                    ) : null,
            },
            {
                field: 'notifications',
                headerName: t('sessionNotifications'),
                sortable: false,
                minWidth: 125,
                flex: 1,
                cellStyle: { display: 'flex', alignItems: 'center' },
                valueGetter: (params) => params.data?.notifications,
                cellRenderer: SessionTableNotificationCellRenderer,
            },
            {
                field: 'mainDomicile',
                headerName: t('sessionCountryOfResidence'),
                maxWidth: 160,
            },
            {
                field: 'rmFullName',
                headerName: t('sessionAssignedRM'),
                minWidth: 220,
            },
            {
                field: 'updatedAt',
                headerName: t('sessionLastModified'),
                sort: 'desc',
                sortIndex: 0,
                valueFormatter: ({ value }) => (!value ? '' : moment(value, 'YYYY-MM-DD').format(DEFAULT_INPUT_DATE_FORMAT)),
            },
            {
                field: 'updatedBy',
                headerName: t('sessionModifiedBy'),
                minWidth: 220,
            },
            {
                headerName: t('sessionAction'),
                sortable: false,
                width: 100,
                cellRenderer: ({ data }: ICellRendererParams<IOptionData>) => (
                    <ZfTextButton disabled={!data!.availableInPcm} size="small" hierarchy="secondary" onClick={() => setSelectedSession(data!)}>
                        {t('open')}
                    </ZfTextButton>
                ),
            },
        ],
        [t],
    );

    const clientSessions: IOptionData[] = useMemo(
        () =>
            sessions?.map((session) => ({
                fullName: `${session?.title} ${session.firstName} ${session.lastName}`,
                partnerId: session.partnerId,
                brNumbers: combineTwoItemsIntoOne(session.brNumbers ?? []).toString(),
                brUuids: session.brUuids,
                notifications: session.partnerUuid ? (notifications?.[session.partnerUuid] ?? emptyNotifications) : emptyNotifications,
                mainDomicile: session?.mainDomicile?.countryCode,
                rmFullName: session.rmFullName,
                updatedAt: session.updatedAt,
                updatedBy: session.updatedBy,
                // The following properties are not shown in UI (table)
                id: session.id,
                partnerUuid: session.partnerUuid,
                availableInPcm: session.availableInPcm,
            })),
        [sessions, notifications],
    );

    return (
        <AgGridTable
            rowData={clientSessions}
            columnDefs={headCells}
            addTableContainer={true}
            defaultColDef={{ resizable: false, headerStyle: { zIndex: 0 } }}
            screenHeaderPaddingsAndHeight={HEADER_TITLE_AND_PADDINGS_HEIGHT}
        />
    );
};
